import { useEffect, Suspense, useState } from 'react';
import lazyWithRetry from './utilities/lazyWithRetry';
import { auth, firestore } from 'utilities/admin.firebase.utility';
import * as Sentry from '@sentry/react';
import { resetUser, modifyUser } from '@/store/slices/user';
import { onAuthStateChanged } from 'firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import { getDoc, doc } from 'firebase/firestore';
import { resolveAuthenticatedUser } from 'utilities/authBootstrap';
import { Routes, Route, useLocation } from 'react-router-dom';
import 'react-phone-input-2/lib/style.css';
import { MainLoader } from './components/MantineLoader/MantineLoader';
import ErrorBoundary from './components/ErrorBoundary';
import AdminNew from './pages/AdminNew';

import { Platform } from './enums/Platform.enum';

const Auth = lazyWithRetry(() => import('pages/Auth'));
const Dashboard = lazyWithRetry(() => import('layouts/dashboard'));
const Home = lazyWithRetry(() => import('pages/Home'));
const Audit = lazyWithRetry(() => import('pages/Audit'));
const Email = lazyWithRetry(() => import('pages/Email'));
const RefreshToken = lazyWithRetry(() => import('pages/RefreshToken'));
const CreateProgramWizard = lazyWithRetry(
  () => import('pages/Programs/components/Program/CreateProgramWizard'),
);
const NoShowSessions = lazyWithRetry(() => import('pages/SessionsSection/Tab/NoShowSessions'));
const AddCoacheeExcel = lazyWithRetry(() => import('pages/Coachees/components/AddCoacheeExcel'));
const CoacheesByCohort = lazyWithRetry(() => import('pages/Cohort/components/CoacheesByCohort'));
const MidNps = lazyWithRetry(() => import('pages/MidNps'));
const MidNpsNew = lazyWithRetry(() => import('pages/MidNpsNew'));
const FinalNps = lazyWithRetry(() => import('pages/FinalNps'));
const FinalNpsNew = lazyWithRetry(() => import('pages/FinalNpsNew'));
const AddMentorExcel = lazyWithRetry(() => import('pages/Mentors/components/AddMentorExcel'));
const MentoringCohorts = lazyWithRetry(() => import('pages/MentoringCohort'));
const AddMentoringCohort = lazyWithRetry(
  () => import('pages/MentoringCohort/components/Cohort/AddMentoringCohort'),
);
const MenteesByCohort = lazyWithRetry(
  () => import('pages/MentoringCohort/components/MenteesByCohort'),
);
const AddMenteeExcel = lazyWithRetry(() => import('pages/Mentees/components/AddMenteeExcel'));
const MentoringEvaluationsList = lazyWithRetry(() => import('pages/MentoringEvaluationsList'));
const MentoringEvaluationsView = lazyWithRetry(
  () => import('pages/MentoringEvaluationsList/components/MentoringEvaluationsView'),
);
const MentoringSession = lazyWithRetry(() => import('pages/MentoringSession/MentoringSession'));
const CoachesNew = lazyWithRetry(() => import('pages/CoachesNew/CoachesNew'));
const CoacheesNew = lazyWithRetry(() => import('pages/CoacheesNew/CoacheesNew'));
const Coachees = lazyWithRetry(() => import('pages/Coachees'));
const Coaches = lazyWithRetry(() => import('pages/Coaches'));
const NoShowNew = lazyWithRetry(() => import('pages/NoShowNew/NoShowNew'));
const AlignmentSession = lazyWithRetry(() => import('pages/AlignmentSession/AlignmentSession'));
const FocusAreasNew = lazyWithRetry(() => import('pages/FocusAreasNew/FocusAreasNew'));

// Routes
const TabCoachee = lazyWithRetry(() => import('pages/Coachees/components/TabCoachee'));

const TabCoach = lazyWithRetry(() => import('pages/Coaches/components/TabCoach'));

const FocusAreas = lazyWithRetry(() => import('pages/FocusAreas'));
const DetailFocusAreas = lazyWithRetry(
  () => import('@/pages/FocusAreasNew/components/DetailFocusAreas'),
);

const Company = lazyWithRetry(() => import('pages/Company'));
const CompanyNew = lazyWithRetry(() => import('pages/CompanyNew/CompanyNew'));
const AddCompany = lazyWithRetry(() => import('pages/Company/components/TabCompany'));

const AddContact = lazyWithRetry(
  () => import('pages/Company/components/TabContacts/components/Contact'),
);

const Sessions = lazyWithRetry(() => import('pages/Sessions'));
const EditableSessions = lazyWithRetry(() => import('pages/Sessions/EditableSessions'));
const DetailSession = lazyWithRetry(() => import('pages/Sessions/DetailSession'));
const AddSessions = lazyWithRetry(() => import('pages/Sessions/AddSession'));
const RescheduleCalendar = lazyWithRetry(() => import('pages/Sessions/RescheduleSession'));
const ScheduledSession = lazyWithRetry(() => import('pages/Sessions/ScheduledSession'));

const CohortNew = lazyWithRetry(() => import('pages/CohortNew/CohortNew'));
const Cohort = lazyWithRetry(() => import('pages/Cohort'));
const AddCohort = lazyWithRetry(() => import('pages/Cohort/components/Cohort'));

const Admin = lazyWithRetry(() => import('pages/Admin'));
const AddAdmin = lazyWithRetry(() => import('pages/Admin/components/AddAdmin'));

const Reports = lazyWithRetry(() => import('pages/Reports'));

const QuestionsOld = lazyWithRetry(() => import('pages/Questions'));
const Questions = lazyWithRetry(() => import('pages/QuestionsNew/QuestionsNew'));
const UsersNew = lazyWithRetry(() => import('pages/UsersNew/UsersNew'));
const DetailUsers = lazyWithRetry(() => import('pages/UsersNew/components/DetailUsers'));
const BulkExcelCoachees = lazyWithRetry(
  () => import('pages/UsersNew/components/BulkExcelCoachees'),
);
const AddQuestions = lazyWithRetry(() => import('pages/Questions/components/AddQuestions'));

const SessionsSection = lazyWithRetry(() => import('pages/SessionsSection'));

const ProgramsOld = lazyWithRetry(() => import('pages/Programs'));
const Programs = lazyWithRetry(() => import('pages/ProgramsNew/ProgramsNew'));
const TabProgram = lazyWithRetry(() => import('pages/Programs/components/Tabs'));

const TabMentor = lazyWithRetry(() => import('pages/Mentors/components/TabMentor'));

const TabMentee = lazyWithRetry(() => import('pages/Mentees/components/TabMentee'));
const MentoringEvaluations = lazyWithRetry(() => import('pages/MentoringEvaluations/index'));

const MentoringAutoEvaluations = lazyWithRetry(() => import('pages/MentoringAutoevaluationsList'));

const MentoringPrograms = lazyWithRetry(() => import('pages/MentoringPrograms/MentoringPrograms'));
const AddMentoringProgram = lazyWithRetry(
  () => import('pages/MentoringPrograms/components/AddMentoringProgram'),
);
const MentoringCompanies = lazyWithRetry(
  () => import('pages/MentoringCompanies/MentoringCompanies'),
);

/* CLARA */
const ClaraCreateExcel = lazyWithRetry(() => import('pages/Clara/CreateExcel/CreateExcelV2'));
const ClaraUserTable = lazyWithRetry(() => import('pages/Clara/Users/User'));
const ClaraUserDetail = lazyWithRetry(() => import('pages/Clara/UserDetail/UserDetail'));
const ClaraEvaluations = lazyWithRetry(() => import('pages/Clara/Evaluation/EvaluationPage.tsx'));
const ClaraEvaluationDetail = lazyWithRetry(
  () => import('pages/Clara/Evaluation/EvaluationDetail.tsx'),
);
const ClaraNps = lazyWithRetry(() => import('pages/Clara/Nps/NpsPage'));
const ClaraNpsDetail = lazyWithRetry(() => import('pages/Clara/Nps/NpsDetail'));

function App() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const location = useLocation();

  // Track loading state for auth
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // BT-1452: getDoc can fail transiently (corporate networks kill
          // Firestore's WebChannel). Retries + a degraded fallback live in
          // resolveAuthenticatedUser so this callback never hangs or throws.
          const adaptedUser = await resolveAuthenticatedUser(firebaseUser, {
            fetchUserDoc: (uid) => getDoc(doc(firestore, 'users', uid)),
            onExhausted: (error) => Sentry.captureException(error),
          });
          dispatch(modifyUser(adaptedUser));
          Sentry.setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            username: adaptedUser.name || firebaseUser.displayName,
          });
        } else {
          dispatch(resetUser());
          Sentry.setUser(null);
        }
      } catch (error) {
        console.error('Error during authentication state change:', error);
        Sentry.captureException(error);
      } finally {
        setIsAuthLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Show loading screen during initial auth check
  if (isAuthLoading) {
    return <MainLoader />;
  }

  return (
    <Suspense fallback={<MainLoader />}>
      {!user.uid ? (
        <Auth />
      ) : (
        <Dashboard>
          <ErrorBoundary resetKeys={[location.pathname]}>
            <Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />

              {/* Coachees Routes */}
              <Route path="/coachees" element={<CoacheesNew appId={Platform.coaching} />} />
              <Route path="/coacheesOld" element={<Coachees />} />
              <Route path="/coachees">
                <Route path="add" element={<TabCoachee />} />
                <Route path="addExcel" element={<AddCoacheeExcel />} />
                <Route path="edit/:id" element={<TabCoachee />} />
                <Route path="view/:id" element={<TabCoachee />} />
              </Route>

              {/* Coaches Routes */}
              <Route path="/coaches" element={<CoachesNew appId={Platform.coaching} />} />
              <Route path="/coachesOld" element={<Coaches />} />
              <Route path="/coaches">
                <Route path="add" element={<TabCoach />} />
                <Route path="edit/:id" element={<TabCoach />} />
                <Route path="view/:id" element={<TabCoach />} />
              </Route>

              {/* Users Routes */}
              <Route path="/users" element={<UsersNew />} />
              <Route path="/users/bulk-excel-coachees" element={<BulkExcelCoachees />} />
              <Route path="/users/:action" element={<DetailUsers />} />
              <Route path="/users/:action/:id" element={<DetailUsers />} />

              {/* Focus Areas Routes */}
              <Route path="/focusareas" element={<FocusAreasNew />} />
              <Route path="/focusareasOld" element={<FocusAreas />} />
              <Route path="/focusareas/:action/:id" element={<DetailFocusAreas />} />
              <Route path="/focusareas/:action" element={<DetailFocusAreas />} />

              {/* Companies Routes */}
              <Route path="/companiesOld" element={<Company />} />
              <Route path="/companies" element={<CompanyNew />} />
              <Route path="/companies">
                <Route path="add" element={<AddCompany />} />
                <Route path="edit/:id" element={<AddCompany />} />
                <Route path="view/:id" element={<AddCompany />} />
                <Route path="edit/:id/contacts">
                  <Route path="add" element={<AddContact />} />
                  <Route path="edit/:idcontact" element={<AddContact />} />
                  <Route path="view/:idcontact" element={<AddContact />} />
                </Route>
                <Route path="view/:id/contacts">
                  <Route path="add" element={<AddContact />} />
                  <Route path="edit/:idcontact" element={<AddContact />} />
                  <Route path="view/:idcontact" element={<AddContact />} />
                </Route>
              </Route>

              {/* Cohorts Routes */}
              <Route path="/cohorts" element={<CohortNew />} />
              <Route path="/cohortsOld" element={<Cohort />} />
              <Route path="/cohorts/:appId" element={<CohortNew />} />
              {/* Coaching cohort sub-pages */}
              <Route path="/cohorts/coaching/add" element={<AddCohort />} />
              <Route path="/cohorts/coaching/edit/:id" element={<AddCohort />} />
              <Route path="/cohorts/coaching/view/:id" element={<AddCohort />} />
              <Route path="/cohorts/coaching/coachees/view/:id" element={<CoacheesByCohort />} />
              {/* Mentoring cohort sub-pages */}
              <Route path="/cohorts/mentoring/add" element={<AddMentoringCohort />} />
              <Route path="/cohorts/mentoring/edit/:id" element={<AddMentoringCohort />} />
              <Route path="/cohorts/mentoring/view/:id" element={<AddMentoringCohort />} />
              <Route path="/cohorts/mentoring/mentees/view/:id" element={<MenteesByCohort />} />
              <Route path="/cohorts/mentoring/mentees/add" element={<AddMenteeExcel />} />

              {/* Admin Routes */}
              <Route path="/adminsOld" element={<Admin />} />
              <Route path="/admins" element={<AdminNew />} />
              <Route path="/admins">
                <Route path="add" element={<AddAdmin />} />
                <Route path="edit/:id" element={<AddAdmin />} />
                <Route path="view/:id" element={<AddAdmin />} />
              </Route>

              {/* Questions Routes */}
              <Route path="/questionsOld" element={<QuestionsOld />} />
              <Route path="/questions" element={<Questions />} />
              <Route path="/questions">
                <Route path="add" element={<AddQuestions />} />
                <Route path="edit/:id" element={<AddQuestions />} />
                <Route path="view/:id" element={<AddQuestions />} />
              </Route>

              {/* Email Routes */}
              <Route path="/emails" element={<Email />} />

              {/* Session Routes */}
              <Route path="/coachee/session">
                <Route path="view/:id" element={<Sessions />} />
                <Route path="edit/:id" element={<EditableSessions />} />
                <Route path="addSession/:id" element={<AddSessions />} />
                <Route path="editSession/:id" element={<RescheduleCalendar />} />
                <Route path="scheduled/:id" element={<ScheduledSession />} />
              </Route>
              <Route path="/:appId/sessions">
                <Route path=":action/:id" element={<DetailSession />} />
              </Route>
              <Route path="/sessions/:appId" element={<MentoringSession />} />

              {/* Sessions Section Routes */}
              <Route path="/sessionsSection" element={<SessionsSection />} />
              <Route path="/sessionsSection">
                <Route path="noShowOld" element={<NoShowSessions />} />
                <Route path="noShow" element={<NoShowNew />} />
                <Route path="alignment" element={<AlignmentSession />} />
              </Route>

              {/* Programs Routes */}
              <Route path="/programs" element={<Programs />} />
              {/* OLD ROUTE */}
              <Route path="/programsOld" element={<ProgramsOld />} />
              <Route path="/programs">
                <Route path="add" element={<CreateProgramWizard />} />
                <Route path="view/:id" element={<TabProgram />} />
                <Route path="edit/:id" element={<TabProgram />} />
                {/* OLD ROUTES */}
                <Route path="program/view/:id" element={<TabProgram />} />
                <Route path="program/edit/:id" element={<TabProgram />} />
              </Route>

              <Route path="/RefreshToken" element={<RefreshToken />} />

              {/* Reports Routes */}
              <Route path="/reports" element={<Reports />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/midnpsOld" element={<MidNps />} />
              <Route path="/midnps" element={<MidNpsNew />} />
              <Route path="/finalnpsOld" element={<FinalNps />} />
              <Route path="/finalnps" element={<FinalNpsNew />} />

              {/* Mentors Routes */}
              <Route path="/mentor" element={<CoachesNew appId={Platform.mentoring} />} />
              <Route path="/mentors" element={<CoachesNew appId={Platform.mentoring} />} />
              <Route path="/mentor">
                <Route path="add" element={<TabMentor />} />
                <Route path="addExcel" element={<AddMentorExcel />} />
                <Route path="edit/:id" element={<TabMentor />} />
                <Route path="view/:id" element={<TabMentor />} />
              </Route>
              <Route path="/mentors">
                <Route path="add" element={<TabMentor />} />
                <Route path="addExcel" element={<AddMentorExcel />} />
                <Route path="edit/:id" element={<TabMentor />} />
                <Route path="view/:id" element={<TabMentor />} />
              </Route>

              {/* Mentees Routes */}
              <Route path="/mentee" element={<CoacheesNew appId={Platform.mentoring} />} />
              <Route path="/coachee" element={<CoacheesNew appId={Platform.mentoring} />} />
              <Route path="/mentees">
                <Route path="add" element={<TabMentee />} />
                <Route path="addExcel" element={<AddMenteeExcel />} />
                <Route path="edit/:id" element={<TabMentee />} />
                <Route path="view/:id" element={<TabMentee />} />
              </Route>
              <Route path="/coachee">
                <Route path="add" element={<TabMentee />} />
                <Route path="addExcel" element={<AddMenteeExcel />} />
                <Route path="edit/:id" element={<TabMentee />} />
                <Route path="view/:id" element={<TabMentee />} />
              </Route>

              {/* Mentoring Companies Routes */}
              <Route path="/mentoring-companies" element={<MentoringCompanies />} />

              {/* Mentoring Programs Routes */}
              <Route path="/mentoring-programs" element={<MentoringPrograms />} />
              <Route path="/mentoring-programs">
                <Route path="add" element={<AddMentoringProgram />} />
                <Route path="view/:id" element={<AddMentoringProgram />} />
                <Route path="edit/:id" element={<AddMentoringProgram />} />
              </Route>

              {/* Mentoring Evaluations Routes */}
              <Route path="/mentoringevaluations" element={<MentoringEvaluationsList />} />
              <Route path="/mentoringevaluations/view/:id" element={<MentoringEvaluationsView />} />
              <Route path="/mentoringevaluations/add" element={<MentoringEvaluations />} />

              {/* App ID Routes */}
              {/*           <Route path="/:appId/sessions">
              <Route path="view/:id" element={<Sessions />} />
              <Route path="edit/:id" element={<EditableSessions />} />
            </Route> */}
              {/* Clara Routes */}
              <Route path="/clara/users" element={<ClaraUserTable />} />
              <Route path="/clara/users/create/excel" element={<ClaraCreateExcel />} />
              <Route path="/clara/users/:action/:id" element={<ClaraUserDetail />} />
              <Route path="/clara/users/:action/" element={<ClaraUserDetail />} />
              <Route path="/clara/evaluations" element={<ClaraEvaluations />} />
              <Route path="/clara/evaluations/:action/:id" element={<ClaraEvaluationDetail />} />
              <Route path="/clara/evaluations/:action/" element={<ClaraEvaluationDetail />} />
              <Route path="/clara/nps" element={<ClaraNps />} />
              <Route path="/clara/nps/:action/:id" element={<ClaraNpsDetail />} />
              <Route path="/clara/nps/:action/" element={<ClaraNpsDetail />} />
            </Routes>
          </ErrorBoundary>
        </Dashboard>
      )}
    </Suspense>
  );
}

export default App;
